import React, { Component } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { FLOW_TYPE } from '../AppConstants';
import SimpleHeader from '../../components/SimpleHeader/SimpleHeader';
import HermesHeader from '../../components/HermesHeader/HermesHeader';

import { connect } from 'react-redux';
import { scrollToTop, setCurrentPage } from './SkeletonPageActions';
import { incrementPageCount } from '../../store/PageCount/PageCountActions';
import { getServerData } from '../../store/ServerData/ServerDataActions';
import { recordImpression } from '../../store/FPTI/FPTIActions';
import FullPageSpinner from '../../components/FullPageSpinner/FullPageSpinner';
import { SPINNER_DEFAULT_MESSAGE_TYPE } from '../../components/FullPageSpinner/FullPageSpinnerConstants';
import { spinnerClose, spinnerOpen } from '../../components/FullPageSpinner/FullPageSpinnerActions';

export class SkeletonPage extends Component {
    static propTypes = {
        _csrf: PropTypes.string,
        browserType: PropTypes.string,
        browserVersion: PropTypes.string,
        children: PropTypes.element,
        contextType: PropTypes.string,
        correlationId: PropTypes.string,
        country: PropTypes.string,
        device: PropTypes.string,
        dispatch: PropTypes.func,
        ErrorPage: PropTypes.func,
        flow: PropTypes.string,
        pageCount: PropTypes.number,
        routing: PropTypes.object,
        viewName: PropTypes.string,
        serverDataUrl: PropTypes.string,
        applicationResponse: PropTypes.string
    };
    constructor(props) {
        super(props);
        this.state = {
            helmetInitialized: false
        };
    }
    componentDidMount() {
        const {
            dispatch,
            pageCount,
            routing,
            serverDataUrl,
            applicationResponse,
            contextType,
            country,
            device,
            flow,
            viewName,
        } = this.props;
        const { pathname } = routing.location;
        const pageName = getPageNameFromPath(pathname);

        // avoid overwriting existing classes on body tag that can come
        // from 3rd party places like cookie banner. Use classList.add
        document.body.classList.add('page');
        [device, country, contextType].forEach(val => {
            if (val) {
                document.body.classList.add(val);
            }
        });

        if (viewName) {
            //todo remove prior page name if exists
            document.body.classList.add(`${viewName.replace(/\s+/g, '')}Page`);
        }
        if (flow === FLOW_TYPE.TESLA) {
            document.body.classList.add('tesla');
        }
        if (flow === FLOW_TYPE.VENICE) {
            document.body.classList.add('venice');
        }

        if (pageCount && serverDataUrl) {
            dispatch(spinnerOpen(SPINNER_DEFAULT_MESSAGE_TYPE));
            dispatch(getServerData(serverDataUrl))
                .then(dispatch(spinnerClose()));
        }
        dispatch(setCurrentPage(pageName));
        dispatch(incrementPageCount());
        dispatch(scrollToTop(false));

        // send client side page impression
        recordImpression(pageName, applicationResponse);

        // if the gdpr banner was not accepted, and a mobile user has triggered an "onfocus"
        // event for a <select /> element, then we need to "re-show" the banner when new
        // routes/pages are loaded/displayed.
        // the gdpr team added this "onfocus" behavior for mobile browsers (based on small screen widths)
        // so the cookie banner wouldn't cover the screen when the keyboard pops up. when they made
        // this change, they also exposed `window.showGdprBanner()` to force it to show again and
        // `window.hideGdprBanner()` as well, but those functions only take effect when the window
        // is narrow enough. for more info, visit `#gdpr-cookie-mgmt` in slack, and read the
        // comments from May 23, 2018
        if (typeof window.showGdprBanner === 'function') {
            window.showGdprBanner();
        }
    }
    onChangeClientState = (newState, addedTags, removedTags) => {
        // we have mounted, and helmet has set some body attributes which our CSS relies on.
        // we only want to show page content once these attributes have been set or else we
        // might see styles "flickering" (like blue CTABanners flashing blue before turning white).
        this.setState({ helmetInitialized: true });
    }
    render() {
        const {
            browserType,
            browserVersion,
            children,
            contextType,
            correlationId,
            country,
            device,
            ErrorPage,
            flow,
            viewName,
        } = this.props;
        const bodyComponent = viewName === 'error' ? <ErrorPage /> : children;

        // only show pageContent after helmet has set our initial attributes
        let pageContent;
        if (this.state.helmetInitialized) {
            pageContent = (
                <React.Fragment>
                    { (flow === FLOW_TYPE.TESLA || flow === FLOW_TYPE.VENICE) && <SimpleHeader flow={flow} /> }
                    { (flow === FLOW_TYPE.HERMES) && <HermesHeader /> }
                    {bodyComponent}
                </React.Fragment>
            );
        }

        return (
            <div>
                <div>
                    <Helmet onChangeClientState={this.onChangeClientState}>
                        <body
                            data-browser={browserType}
                            data-browserversion={browserVersion}
                            data-contexttype={contextType}
                            data-correlation-id={correlationId}
                            data-country={country}
                            data-flow={flow}
                            data-pagename={viewName}
                        />
                    </Helmet>
                    {pageContent}
                    <FullPageSpinner />
                </div>
            </div>
        );
    }
}

export const getPageNameFromPath = (path) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1] || splitPath[splitPath.length - 2];
};

export const mapStateToProps = state => ({
    browserType: state.serverData.browserType,
    browserVersion: state.serverData.browserVersion,
    contextType: state.serverData.contextType,
    correlationId: state.serverData.correlationId,
    _csrf: state.serverData._csrf,
    country: state.serverData.country,
    device: state.serverData.device,
    flow: state.serverData.flow,
    pageCount: state.pageCount,
    routing: state.router,
    viewName: _.get(state, 'skeletonPage.currentPage', state.serverData.viewName),
    applicationResponse: _.toUpper(_.get(state, 'serverData.decisionModel.applicationResponse', ''))
});

export default connect(mapStateToProps)(SkeletonPage);
